import { type ReactNode } from 'react';
import { Cards, Info, Skeleton, Stamp } from '../../../../modules/design-system';
import type { PoolShippingService } from '../../../../modules/api';

export interface ShippingOptionsSectionProps {
  services: PoolShippingService[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** No cards picked yet → can't quote anything; prompt to choose items. */
  hasItems: boolean;
  /** No address yet → can't quote the carrier rate; prompt for an address. */
  hasAddress: boolean;
  /** Full EasyPost price per option (service id → $), backend-quoted. */
  optionPrices?: Record<number, number>;
  /** Quote in flight → show loaders in place of the per-option prices. */
  loading?: boolean;
  /** Clicking the "choose items" empty state opens the in-page item picker. */
  onChooseItems?: () => void;
  /** Clicking the "enter address" empty state opens the address flow. */
  onChooseAddress?: () => void;
}

/**
 * Centered icon + copy block used for the two pre-quote empty states. When
 * `onClick` is given the card becomes a button (hover + pointer) so the prompt
 * is directly actionable — clicking it opens the matching picker.
 */
function EmptyState({
  icon,
  children,
  onClick,
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick?: () => void;
}) {
  const cardStyle = {
    border: '1px solid rgba(171,174,179,0.16)',
    color: 'rgba(255,255,255,0.70)',
  };
  const body = (
    <>
      {icon}
      <p className="mt-3 text-[14px] leading-[20px]">{children}</p>
    </>
  );
  return (
    <>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="mt-3 flex w-full cursor-pointer flex-col items-center justify-center rounded-[20px] bg-white/[0.03] px-4 py-10 text-center transition-colors hover:bg-white/[0.07]"
          style={cardStyle}
        >
          {body}
        </button>
      ) : (
        <div
          className="mt-3 flex flex-col items-center justify-center rounded-[20px] bg-white/[0.03] px-4 py-10 text-center"
          style={cardStyle}
        >
          {body}
        </div>
      )}
      <div
        className="mt-3 flex items-center gap-2 text-[12px]"
        style={{ color: 'rgba(255,255,255,0.45)' }}
      >
        <Info size={16} strokeWidth={1.6} />
        <span>Shipping costs include payment processing fee</span>
      </div>
    </>
  );
}

/**
 * "Delivery Shipping Options" section. Until both items AND an address exist
 * we can't quote, so we show a prompt (choose items first, then address). Once
 * quotable we render the service catalogue as a single-select radio list with
 * the per-option EasyPost price (or a loader while the quote is in flight).
 */
export function ShippingOptionsSection({
  services,
  selectedId,
  onSelect,
  hasItems,
  hasAddress,
  optionPrices = {},
  loading = false,
  onChooseItems,
  onChooseAddress,
}: ShippingOptionsSectionProps) {
  return (
    <section>
      <h3
        className="text-[17px] font-bold tracking-[-0.3px]"
        style={{ color: 'var(--co-MyPacksItemCard-titleColor, #FFFFFF)' }}
      >
        Delivery Shipping Options
      </h3>

      {!hasItems ? (
        <EmptyState
          icon={<Cards size={28} strokeWidth={1.6} />}
          {...(onChooseItems ? { onClick: onChooseItems } : {})}
        >
          <span className="font-semibold text-white">Choose items to ship</span>
          <br />
          to see delivery options
        </EmptyState>
      ) : !hasAddress || services.length === 0 ? (
        <EmptyState
          icon={<Stamp size={28} strokeWidth={1.6} />}
          {...(onChooseAddress ? { onClick: onChooseAddress } : {})}
        >
          <span className="font-semibold text-white">To calculate the cost</span> of delivery,
          <br />
          please enter your address
        </EmptyState>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {services.map((s) => {
            const isActive = s.id === selectedId;
            const price = optionPrices[s.id];
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onSelect(s.id)}
                  aria-pressed={isActive}
                  className="
                    flex w-full items-start justify-between gap-3 rounded-[16px]
                    p-3 text-left transition-colors
                  "
                  style={{
                    background: isActive
                      ? 'rgba(35,209,29,0.10)'
                      : 'rgba(255,255,255,0.04)',
                    border: '1px solid ' + (isActive ? '#23D11D' : 'rgba(255,255,255,0.08)'),
                    color: '#FFFFFF',
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold">{s.name}</div>
                    {s.description && (
                      <div
                        className="mt-0.5 text-[12px]"
                        style={{ color: 'rgba(255,255,255,0.55)' }}
                      >
                        {s.description}
                      </div>
                    )}
                  </div>
                  {loading ? (
                    <Skeleton className="mt-0.5 h-[18px] w-[52px] shrink-0 rounded" />
                  ) : (
                    <div
                      className="shrink-0 text-[14px] font-semibold tabular-nums"
                      style={price == null ? { color: 'rgba(255,255,255,0.55)' } : undefined}
                    >
                      {price == null ? 'TBD' : `$${price.toFixed(2)}`}
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
